import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from './use-auth'
import { authorizedFetch } from '../lib/api'
import { API_ORIGIN } from '../constants'

export type FeedbackKind =
  | 'bug'
  | 'feature_request'
  | 'change_request'
  | 'agent_performance'
  | 'onboarding'
  | 'general'

export type FeedbackElicitation = 'volunteered' | 'prompted' | 'derived'

export type FeedbackVisibility = 'shared' | 'platform'

export type FeedbackPromptScope = 'board' | 'onboarding'

export type FeedbackStatus =
  | 'new'
  | 'acknowledged'
  | 'planned'
  | 'in_progress'
  | 'done'
  | 'declined'
  | 'duplicate'

export interface FeedbackItem {
  id: string
  kind: FeedbackKind
  elicitation: FeedbackElicitation
  visibility?: FeedbackVisibility
  title: string
  body: string
  status: FeedbackStatus
  vote_count: number
  duplicate_of: string | null
  created_at: string
  updated_at: string
  my_vote?: boolean
}

export interface FeedbackAdminItem extends FeedbackItem {
  visibility: FeedbackVisibility
  reporter: { id: string; email: string | null } | null
  thread_id: string | null
  message_id: string | null
  run_id: string | null
  prompt_id: string | null
  external_ref: string | null
  linear_url: string | null
  linear_synced_at: string | null
  linear_sync_error: string | null
  linear_sync_attempts: number
  linear_priority: number | null
  linear_assignee_email: string | null
}

export interface FeedbackPrompt {
  id: string
  question: string
  kind_hint: FeedbackKind | null
  scope?: FeedbackPromptScope
  active: boolean
  created_at: string
  response_count?: number
}

export interface MessageRatingAdmin {
  id: string
  run_id: string
  score: number
  tags: string[]
  comment: string
  created_at: string | null
  user: { id: string; email: string | null } | null
  promoted_item_id: string | null
}

interface FeedbackItemsResponse<T> {
  count: number
  items: T[]
}

export interface FeedbackItemsFilters {
  kind?: FeedbackKind | null
  status?: FeedbackStatus | null
  visibility?: FeedbackVisibility | null
  sort?: 'votes' | 'recent'
}

async function parseOrThrow(response: Response, fallbackMessage: string) {
  const data = await response.json().catch(() => null)
  if (!response.ok) {
    throw new Error(data?.error || fallbackMessage)
  }
  return data
}

export function useFeedbackItems(filters: FeedbackItemsFilters = {}) {
  const { token } = useAuth()
  const { kind, status, sort = 'votes' } = filters

  const query = useQuery({
    queryKey: ['feedback-items', kind ?? 'all', status ?? 'all', sort],
    queryFn: async (): Promise<FeedbackItemsResponse<FeedbackItem>> => {
      if (!token) throw new Error('Not authenticated')
      const params = new URLSearchParams({ sort, limit: '100' })
      if (kind) params.append('kind', kind)
      if (status) params.append('status', status)
      const response = await authorizedFetch(`${API_ORIGIN}/api/feedback/items?${params}`)
      return parseOrThrow(response, 'Failed to fetch feedback')
    },
    enabled: !!token,
    staleTime: 30 * 1000,
  })

  return {
    items: query.data?.items ?? [],
    total: query.data?.count ?? 0,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}

export function useVoteFeedbackItem() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({ itemId, vote }: { itemId: string; vote: boolean }): Promise<FeedbackItem> => {
      if (!token) throw new Error('Not authenticated')
      const response = await authorizedFetch(`${API_ORIGIN}/api/feedback/items/${itemId}/vote`, {
        method: vote ? 'POST' : 'DELETE',
      })
      return parseOrThrow(response, 'Failed to update vote')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback-items'] })
      queryClient.invalidateQueries({ queryKey: ['feedback-admin-items'] })
    },
  })

  return {
    voteItem: mutation.mutate,
    isVoting: mutation.isPending,
  }
}

export interface SubmitFeedbackData {
  kind: FeedbackKind
  title: string
  body?: string
}

export function useSubmitFeedback() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (data: SubmitFeedbackData): Promise<FeedbackItem> => {
      if (!token) throw new Error('Not authenticated')
      const response = await authorizedFetch(`${API_ORIGIN}/api/feedback/items`, {
        method: 'POST',
        body: JSON.stringify(data),
      })
      return parseOrThrow(response, 'Failed to submit feedback')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback-items'] })
    },
  })

  return {
    submitFeedback: mutation.mutate,
    isSubmitting: mutation.isPending,
    submitError: mutation.error,
  }
}

/** Active team-authored prompts this user hasn't answered or dismissed. */
export function usePendingFeedbackPrompts(scope: FeedbackPromptScope = 'board') {
  const { token } = useAuth()

  const query = useQuery({
    // Prefixed by 'feedback-prompts' so the SSE content_update handler
    // (mapper cacheInvalidations) clears the badge live after answer/dismiss.
    queryKey: ['feedback-prompts', 'pending', scope],
    queryFn: async (): Promise<{ prompts: FeedbackPrompt[] }> => {
      if (!token) throw new Error('Not authenticated')
      const params = new URLSearchParams({ scope })
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/feedback/prompts/pending?${params}`,
      )
      return parseOrThrow(response, 'Failed to fetch pending prompts')
    },
    enabled: !!token,
    staleTime: 60 * 1000,
  })

  return {
    prompts: query.data?.prompts ?? [],
    isLoading: query.isLoading,
  }
}

export function useAnswerFeedbackPrompt() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({
      promptId,
      title,
      body,
      kind,
    }: {
      promptId: string
      title: string
      body?: string
      kind?: FeedbackKind
    }): Promise<FeedbackItem> => {
      if (!token) throw new Error('Not authenticated')
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/feedback/prompts/${promptId}/answer`,
        {
          method: 'POST',
          body: JSON.stringify({ title, body, kind }),
        },
      )
      return parseOrThrow(response, 'Failed to answer prompt')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback-prompts'] })
      queryClient.invalidateQueries({ queryKey: ['feedback-admin-items'] })
    },
  })

  return {
    answerPrompt: mutation.mutateAsync,
    isAnswering: mutation.isPending,
  }
}

/** Dismiss a team prompt ("don't ask me this again"); clears it from the badge count. */
export function useDismissFeedbackPrompt() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (promptId: string): Promise<{ success: boolean }> => {
      if (!token) throw new Error('Not authenticated')
      const response = await authorizedFetch(`${API_ORIGIN}/api/feedback/prompts/${promptId}/dismiss`, {
        method: 'POST',
      })
      return parseOrThrow(response, 'Failed to dismiss prompt')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback-prompts'] })
    },
  })

  return {
    dismissPrompt: mutation.mutate,
    dismissPromptAsync: mutation.mutateAsync,
    isDismissing: mutation.isPending,
  }
}

// ==========================================
// Administrator hooks
// ==========================================

export function useAdminFeedbackItems(filters: FeedbackItemsFilters = {}) {
  const { token } = useAuth()
  const { kind, status, visibility, sort = 'recent' } = filters

  const query = useQuery({
    queryKey: [
      'feedback-admin-items',
      kind ?? 'all',
      status ?? 'all',
      visibility ?? 'all',
      sort,
    ],
    queryFn: async (): Promise<FeedbackItemsResponse<FeedbackAdminItem>> => {
      if (!token) throw new Error('Not authenticated')
      const params = new URLSearchParams({ sort, limit: '100' })
      if (kind) params.append('kind', kind)
      if (status) params.append('status', status)
      if (visibility) params.append('visibility', visibility)
      const response = await authorizedFetch(`${API_ORIGIN}/api/feedback/admin/items?${params}`)
      return parseOrThrow(response, 'Failed to fetch feedback')
    },
    enabled: !!token,
    staleTime: 30 * 1000,
  })

  return {
    items: query.data?.items ?? [],
    total: query.data?.count ?? 0,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}

export function useAdminMessageRatings() {
  const { token } = useAuth()

  const query = useQuery({
    queryKey: ['feedback-admin-ratings'],
    queryFn: async (): Promise<{ count: number; ratings: MessageRatingAdmin[] }> => {
      if (!token) throw new Error('Not authenticated')
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/feedback/admin/ratings?limit=100`,
      )
      return parseOrThrow(response, 'Failed to fetch ratings')
    },
    enabled: !!token,
    staleTime: 30 * 1000,
  })

  return {
    ratings: query.data?.ratings ?? [],
    total: query.data?.count ?? 0,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}

export function usePromoteMessageRating() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({
      ratingId,
      title,
    }: {
      ratingId: string
      title: string
    }): Promise<FeedbackAdminItem> => {
      if (!token) throw new Error('Not authenticated')
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/feedback/admin/ratings/${ratingId}/promote`,
        {
          method: 'POST',
          body: JSON.stringify({ title }),
        },
      )
      return parseOrThrow(response, 'Failed to promote rating')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback-admin-ratings'] })
      queryClient.invalidateQueries({ queryKey: ['feedback-admin-items'] })
      queryClient.invalidateQueries({ queryKey: ['feedback-items'] })
    },
  })

  return {
    promoteRating: mutation.mutate,
    isPromoting: mutation.isPending,
  }
}

export interface AdminUpdateItemData {
  status?: FeedbackStatus
  kind?: FeedbackKind
  duplicate_of?: string
}

export function useAdminUpdateFeedbackItem() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({ itemId, data }: { itemId: string; data: AdminUpdateItemData }): Promise<FeedbackAdminItem> => {
      if (!token) throw new Error('Not authenticated')
      const response = await authorizedFetch(`${API_ORIGIN}/api/feedback/admin/items/${itemId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      })
      return parseOrThrow(response, 'Failed to update feedback item')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback-items'] })
      queryClient.invalidateQueries({ queryKey: ['feedback-admin-items'] })
    },
  })

  return {
    updateItem: mutation.mutate,
    isUpdating: mutation.isPending,
  }
}

export function useAdminRetryLinearSync() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (itemId: string): Promise<FeedbackAdminItem> => {
      if (!token) throw new Error('Not authenticated')
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/feedback/admin/items/${itemId}/linear/retry`,
        { method: 'POST' },
      )
      return parseOrThrow(response, 'Failed to retry Linear sync')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback-admin-items'] })
    },
  })

  return {
    retryLinearSync: mutation.mutate,
    isRetrying: mutation.isPending,
  }
}

export function useFeedbackPrompts() {
  const { token } = useAuth()

  const query = useQuery({
    queryKey: ['feedback-prompts'],
    queryFn: async (): Promise<{ prompts: FeedbackPrompt[] }> => {
      if (!token) throw new Error('Not authenticated')
      const response = await authorizedFetch(`${API_ORIGIN}/api/feedback/admin/prompts`)
      return parseOrThrow(response, 'Failed to fetch prompts')
    },
    enabled: !!token,
    staleTime: 30 * 1000,
  })

  return {
    prompts: query.data?.prompts ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}

export function useCreateFeedbackPrompt() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (data: {
      question: string
      kind_hint?: FeedbackKind
      scope?: FeedbackPromptScope
    }): Promise<FeedbackPrompt> => {
      if (!token) throw new Error('Not authenticated')
      const response = await authorizedFetch(`${API_ORIGIN}/api/feedback/admin/prompts`, {
        method: 'POST',
        body: JSON.stringify(data),
      })
      return parseOrThrow(response, 'Failed to create prompt')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback-prompts'] })
    },
  })

  return {
    createPrompt: mutation.mutate,
    isCreating: mutation.isPending,
  }
}

export function useUpdateFeedbackPrompt() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({
      promptId,
      data,
    }: {
      promptId: string
      data: {
        question?: string
        kind_hint?: FeedbackKind | ''
        active?: boolean
        scope?: FeedbackPromptScope
      }
    }): Promise<FeedbackPrompt> => {
      if (!token) throw new Error('Not authenticated')
      const response = await authorizedFetch(`${API_ORIGIN}/api/feedback/admin/prompts/${promptId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      })
      return parseOrThrow(response, 'Failed to update prompt')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback-prompts'] })
    },
  })

  return {
    updatePrompt: mutation.mutate,
    isUpdating: mutation.isPending,
  }
}
